import React, { useState, useEffect } from 'react';
import { ArrowLeft, Save, Upload, FileText, X, RotateCcw, Plus, Trash2, Building2, Navigation, Download, FileSpreadsheet, AlertCircle, CheckCircle, XCircle, ExternalLink } from 'lucide-react';
import * as XLSX from 'xlsx';
import toast from 'react-hot-toast';

// Import services
import { 
  createSPK, 
  updateSPK, 
  getSPKById,  // ← Import this!
  uploadDocument,
  SPKStatus,
  ProjectType 
} from '../../../services/spkService';
import { getAllCustomers } from '../../../services/customerService';

const SPKForm = ({ onBack, spkData = null, mode = 'add' }) => {
  // Form Data State
  const [formData, setFormData] = useState({
    id: spkData?.id || null,  // ← ADD THIS LINE!
    customerId: spkData?.customerId || null,
    spkNumber: spkData?.spkNumber || '',
    spkDate: spkData?.spkDate || '',
    projectType: spkData?.projectType || ProjectType.TOWER,
    projectName: spkData?.projectName || '',
    deliveryPeriod: spkData?.deliveryPeriod || null,
    notes: spkData?.notes || '',
    status: spkData?.status || SPKStatus.ACCEPTED,
    towerSiteIds: spkData?.towerSites?.map(site => site.id) || [],
    foSegmentIds: spkData?.foSegments?.map(seg => seg.id) || []
  });

  // Multiple documents state (for new uploads)
  const [documents, setDocuments] = useState([]);
  
  // Existing documents (for edit mode)
  const [existingDocuments, setExistingDocuments] = useState(spkData?.documents || []);
  
  // Sites/Segments state
  const [items, setItems] = useState(
    formData.projectType === ProjectType.TOWER 
      ? (spkData?.towerSites || [])
      : (spkData?.foSegments || [])
  );

  // Customer list
  const [customers, setCustomers] = useState([]);

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showImportSummary, setShowImportSummary] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [showDocumentModal, setShowDocumentModal] = useState(false);

  // Fetch customers on mount
  useEffect(() => {
    fetchCustomers();
  }, []);

    // ============================================================
    // LOAD SPK DATA IN EDIT MODE
    // ============================================================
    useEffect(() => {
    const loadSPKData = async () => {
        if (mode === 'edit' && spkData) {
        try {
            console.log('📥 Loading SPK data from props:', spkData);
            
            // Update formData with ID from spkData
            setFormData(prev => ({
            ...prev,
            id: spkData.id  // ← ADD ID HERE!
            }));
            
            toast.success('Data SPK berhasil dimuat');
        } catch (error) {
            console.error('❌ Failed to load SPK data:', error);
            toast.error('Gagal memuat data SPK');
        }
        }
    };
    
    loadSPKData();
    }, [mode, spkData]);

  const fetchCustomers = async () => {
    try {
      const response = await getAllCustomers();
      setCustomers(response);
    } catch (error) {
      console.error('Error fetching customers:', error);
      toast.error('Gagal memuat data customer');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }

    // Reset items when project type changes
    if (name === 'projectType') {
      setItems([]);
      setErrors(prev => ({ ...prev, items: '' }));
    }
  };

  const handleAddDocument = (newDocument) => {
    setDocuments([...documents, { 
      id: Date.now(), 
      file: newDocument.file,  // Store actual File object
      fileName: newDocument.fileName,
      fileType: newDocument.fileType,
      description: newDocument.description,
      fileUrl: newDocument.fileUrl  // Preview URL
    }]);
    setShowDocumentModal(false);
    if (errors.documents) {
      setErrors(prev => ({ ...prev, documents: '' }));
    }
  };

  const handleRemoveDocument = (docId) => {
    setDocuments(documents.filter(doc => doc.id !== docId));
  };

  const handleAddItem = (newItem) => {
    setItems([...items, { ...newItem, id: Date.now() }]);
    setShowAddModal(false);
    if (errors.items) {
      setErrors(prev => ({ ...prev, items: '' }));
    }
  };

  const handleRemoveItem = (itemId) => {
    setItems(items.filter(item => item.id !== itemId));
  };

  // Download Excel Template
  const handleDownloadTemplate = () => {
    const template = [
      {
        'No': 1,
        'Span Id': 'S000049979',
        'Segment ID': 'SUM-SU-KIS-2009 <> SUM-SU-KIS-8234',
        'Ring Id / Property Number': 'R0000017012',
        'Site Name A & B': 'AIR BATU_ASAHAN <> SF-NMDN_0166-Air T Esa Super34',
        'Ring Status': 'CRTD',
        'Ring Type': 'Insert',
        'SPK Id': '4229',
        'Operator': 'XLSmart',
        'Kota/Kabupaten': 'ASAHAN',
        'Main Area': 'SUMATERA UTARA',
        'Pulau': 'SUMATERA',
        'Contract Operator': 'MoM - June 2025',
        'SPK Date': '30-06-2025',
        'Plan Distance Site to Site': 1.970,
        'Plan Distance Site to Closure': 1.870,
        'Actual Distance Site to Site': '',
        'Actual Distance Site to Closure': '',
        'Commercial Distance': 1.870,
        'Initial Core': 4,
        'Project Status': '11. ATP'
      },
      {
        'No': 2,
        'Span Id': 'S000049980',
        'Segment ID': 'SUM-SU-KIS-8234 <> SUM-SU-KIS-0151',
        'Ring Id / Property Number': 'R0000017013',
        'Site Name A & B': 'SF-NMDN_0166-Air T Esa Super34 <> Air Batu',
        'Ring Status': 'CRTD',
        'Ring Type': 'Insert',
        'SPK Id': '4229',
        'Operator': 'XLSmart',
        'Kota/Kabupaten': 'ASAHAN',
        'Main Area': 'SUMATERA UTARA',
        'Pulau': 'SUMATERA',
        'Contract Operator': 'MoM - June 2025',
        'SPK Date': '30-06-2025',
        'Plan Distance Site to Site': 11.249,
        'Plan Distance Site to Closure': 1.870,
        'Actual Distance Site to Site': '',
        'Actual Distance Site to Closure': '',
        'Commercial Distance': 1.870,
        'Initial Core': 4,
        'Project Status': '11. ATP'
      }
    ];

    const ws = XLSX.utils.json_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'FO Segments');
    
    // Set column widths
    ws['!cols'] = [
      { wch: 5 },  // No
      { wch: 15 }, // Span Id
      { wch: 40 }, // Segment ID
      { wch: 20 }, // Ring Id
      { wch: 50 }, // Site Name A & B
      { wch: 12 }, // Ring Status
      { wch: 12 }, // Ring Type
      { wch: 10 }, // SPK Id
      { wch: 15 }, // Operator
      { wch: 20 }, // Kota/Kabupaten
      { wch: 20 }, // Main Area
      { wch: 15 }, // Pulau
      { wch: 20 }, // Contract Operator
      { wch: 15 }, // SPK Date
      { wch: 15 }, // Plan Distance Site to Site
      { wch: 15 }, // Plan Distance Site to Closure
      { wch: 15 }, // Actual Distance Site to Site
      { wch: 15 }, // Actual Distance Site to Closure
      { wch: 15 }, // Commercial Distance
      { wch: 12 }, // Initial Core
      { wch: 15 }  // Project Status
    ];

    XLSX.writeFile(wb, 'FO_Segment_Template.xlsx');
  };

  // Upload and Parse Excel
  const handleExcelUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const workbook = XLSX.read(event.target.result, { type: 'binary' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const data = XLSX.utils.sheet_to_json(worksheet);

        processExcelData(data);
      } catch (error) {
        toast.error('Error reading Excel file. Please check the file format.');
        console.error(error);
      }
    };
    reader.readAsBinaryString(file);
    
    // Reset input
    e.target.value = '';
  };

  const processExcelData = (data) => {
    const result = {
      success: [],
      skipped: [],
      errors: []
    };

    const existingSpanIds = new Map(items.map(item => [item.spanId, item]));

    data.forEach((row, index) => {
      const rowNumber = index + 2; // Excel row number (header is row 1)
      
      // Skip empty rows
      const isEmpty = !row['Span Id'] && !row['Site Name A & B'] && !row['Main Area'] && 
                      !row['Kota/Kabupaten'] && !row['Commercial Distance'] && !row['Ring Type'];
      
      if (isEmpty) {
        result.skipped.push({
          row: rowNumber,
          identifier: '-',
          reason: 'Empty row'
        });
        return;
      }

      // Validate mandatory fields
      const mandatoryFields = {
        'Span Id': row['Span Id'],
        'Site Name A & B': row['Site Name A & B'],
        'Main Area': row['Main Area'],
        'Kota/Kabupaten': row['Kota/Kabupaten'],
        'Commercial Distance': row['Commercial Distance'],
        'Ring Type': row['Ring Type']
      };

      const missingFields = Object.entries(mandatoryFields)
        .filter(([_, value]) => !value)
        .map(([key, _]) => key);

      if (missingFields.length > 0) {
        result.errors.push({
          row: rowNumber,
          identifier: row['Span Id'] || row['Site Name A & B'] || 'Unknown',
          reason: `Missing mandatory fields: ${missingFields.join(', ')}`
        });
        return;
      }

      // Validate Ring Type
      const validRingTypes = ['Insert', 'Add Span', 'Star'];
      const ringType = row['Ring Type']?.trim();
      if (!validRingTypes.includes(ringType)) {
        result.errors.push({
          row: rowNumber,
          identifier: row['Span Id'],
          reason: `Invalid Ring Type: "${ringType}". Must be one of: ${validRingTypes.join(', ')}`
        });
        return;
      }

      // Validate and convert Commercial Distance to meters
      const commercialDistance = parseFloat(row['Commercial Distance']);
      if (isNaN(commercialDistance) || commercialDistance <= 0) {
        result.errors.push({
          row: rowNumber,
          identifier: row['Span Id'],
          reason: `Invalid Commercial Distance: "${row['Commercial Distance']}". Must be a positive number.`
        });
        return;
      }

      // Convert km to meters (assuming Excel has km values)
      const lengthInMeters = commercialDistance * 1000;

      const segment = {
        id: Date.now() + index,
        spanId: row['Span Id']?.trim(),
        segmentName: row['Site Name A & B']?.trim(),
        area: row['Main Area']?.trim(),
        kota: row['Kota/Kabupaten']?.trim(),
        length: lengthInMeters,
        ringType: ringType
      };

      // Check if segment with same Span ID exists
      if (existingSpanIds.has(segment.spanId)) {
        // Update existing segment
        existingSpanIds.set(segment.spanId, segment);
        result.success.push({
          row: rowNumber,
          identifier: segment.spanId,
          action: 'Updated'
        });
      } else {
        // Add new segment
        existingSpanIds.set(segment.spanId, segment);
        result.success.push({
          row: rowNumber,
          identifier: segment.spanId,
          action: 'Added'
        });
      }
    });

    // Update items with new/updated segments
    setItems(Array.from(existingSpanIds.values()));

    // Show import summary
    setImportResult(result);
    setShowImportSummary(true);

    // Clear items error if we have successful imports
    if (result.success.length > 0 && errors.items) {
      setErrors(prev => ({ ...prev, items: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Basic validation
    if (!formData.customerId) newErrors.customerId = 'Customer is required';
    if (!formData.spkNumber.trim()) newErrors.spkNumber = 'SPK Number is required';
    if (!formData.spkDate) newErrors.spkDate = 'SPK Date is required';
    if (!formData.projectName.trim()) newErrors.projectName = 'Project Name is required';
    if (!formData.deliveryPeriod || formData.deliveryPeriod <= 0) {
      newErrors.deliveryPeriod = 'Delivery period must be greater than 0';
    }
    if (documents.length === 0 && mode === 'add' && existingDocuments.length === 0) {
      newErrors.documents = 'At least one SPK document is required';
    }
    
    // Items validation
    if (items.length === 0) {
      newErrors.items = `At least one ${formData.projectType === ProjectType.TOWER ? 'tower site' : 'segment'} is required`;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

{/*}  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error('Mohon lengkapi semua field yang diperlukan');
      return;
    }
    
    setIsSubmitting(true);
    try {
      let spkResponse;
      
      // Step 1: Create or Update SPK
      if (mode === 'add') {
        spkResponse = await createSPK({
          customerId: parseInt(formData.customerId),
          spkNumber: formData.spkNumber,
          spkDate: formData.spkDate,
          projectType: formData.projectType,
          projectName: formData.projectName,
          deliveryPeriod: parseInt(formData.deliveryPeriod),
          notes: formData.notes,
          status: formData.status,
          towerSiteIds: formData.towerSiteIds,
          foSegmentIds: formData.foSegmentIds
        });
        toast.success('SPK berhasil dibuat!');
      } else {
        spkResponse = await updateSPK(spkData.id, {
          spkNumber: formData.spkNumber,
          spkDate: formData.spkDate,
          projectType: formData.projectType,
          projectName: formData.projectName,
          deliveryPeriod: parseInt(formData.deliveryPeriod),
          notes: formData.notes,
          status: formData.status
        });
        toast.success('SPK berhasil diupdate!');
      }
      
      // Step 2: Upload documents (if any new documents)
      if (documents.length > 0 && mode === 'add') {
        const uploadPromises = documents.map(doc => 
          uploadDocument(spkResponse.id, doc.file, doc.description)
        );
        await Promise.all(uploadPromises);
        toast.success(`${documents.length} dokumen berhasil diupload!`);
      }
      
      if (onBack) onBack();
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Gagal menyimpan SPK. Silakan coba lagi.');
    } finally {
      setIsSubmitting(false);
    }
  };
*/}

// ============================================================
// COMPLETE FIXED handleSubmit for SPKForm.jsx
// ============================================================
// This version handles ALL edge cases including:
// - Missing formData.id in EDIT mode
// - Proper ID detection (< 1 trillion)
// - Mode-specific logic (ADD vs EDIT)
// - Complete field mapping (20 fields)

const handleSubmit = async (e) => {
  e.preventDefault();
  
  console.log('='.repeat(80));
  console.log('🚀 HANDLE SUBMIT STARTED');
  console.log('='.repeat(80));
  
  if (!validateForm()) {
    toast.error('Mohon lengkapi semua field yang diperlukan');
    return;
  }
  
  if (mode === 'edit') {
    if (!formData.id || formData.id === 'undefined' || formData.id === undefined) {
      console.error('❌ EDIT mode but formData.id is missing:', formData.id);
      toast.error('Error: SPK ID tidak ditemukan. Tidak bisa update.');
      return;
    }
    console.log('✅ EDIT mode with valid ID:', formData.id);
  }
  
  setIsSubmitting(true);
  
  try {
    console.log('📋 Form Data:', formData);
    console.log('📦 Items:', items);
    console.log('📄 Documents:', documents);
    
    const spkData = {
      customerId: parseInt(formData.customerId),
      spkNumber: formData.spkNumber,
      spkDate: formData.spkDate,
      projectType: formData.projectType,
      projectName: formData.projectName,
      deliveryPeriod: parseInt(formData.deliveryPeriod),
      notes: formData.notes,
      status: formData.status || SPKStatus.ACCEPTED
    };
    
    if (mode === 'add') {
      console.log('🆕 CREATE MODE');
      console.log('🔍 Project Type:', formData.projectType);
      console.log('🔍 Items Count:', items.length);
      
      // Filter items
      const existingItems = items.filter(item => {
        const itemId = item.id;
        const isExisting = typeof itemId === 'number' && itemId < 1000000000000;
        console.log(`  🔍 Item ${itemId} → isExisting: ${isExisting}`);
        return isExisting;
      });
      
      const newItems = items.filter(item => {
        const itemId = item.id;
        const isNew = typeof itemId !== 'number' || itemId >= 1000000000000;
        console.log(`  🔍 Item ${itemId} → isNew: ${isNew}`);
        return isNew;
      });
      
      console.log('📊 Items Breakdown:', {
        total: items.length,
        existing: existingItems.length,
        new: newItems.length,
        projectType: formData.projectType
      });
      
      if (formData.projectType === ProjectType.TOWER) {
        console.log('🏗️ TOWER Project');
        
        if (existingItems.length > 0) {
          spkData.towerSiteIds = existingItems.map(item => item.id);
          console.log('🔗 Linking existing tower sites:', spkData.towerSiteIds);
        }
        
        if (newItems.length > 0) {
          spkData.newTowerSites = newItems.map(item => ({
            siteId: item.siteId,
            siteName: item.siteName,
            location: item.location,
            regional: item.regional,
            towerHeight: parseInt(item.towerHeight),
            typeSiteContract: item.typeSiteContract
          }));
          console.log('🆕 Creating new tower sites:', spkData.newTowerSites);
        }
        
      } else if (formData.projectType === ProjectType.FO) {
        console.log('📡 FO Project');
        
        if (existingItems.length > 0) {
          spkData.foSegmentIds = existingItems.map(item => item.id);
          console.log('🔗 Linking existing FO segments:', spkData.foSegmentIds);
        }
        
        if (newItems.length > 0) {
          console.log('🔨 Building newFOSegments array...');
          
          spkData.newFOSegments = newItems.map((item, idx) => {
            const segment = {
              spanId: item.spanId,
              segmentName: item.segmentName,
              area: item.area,
              kota: item.kota,
              length: parseInt(item.length),
              ringType: item.ringType,
              segmentId: item.segmentId || null,
              ringId: item.ringId || null,
              ringStatus: item.ringStatus || null,
              spkIdReference: item.spkIdReference || null,
              operator: item.operator || null,
              pulau: item.pulau || null,
              contractOperator: item.contractOperator || null,
              spkDate: item.spkDate || null,
              planDistanceSiteToSite: item.planDistanceSiteToSite || null,
              planDistanceSiteToClosure: item.planDistanceSiteToClosure || null,
              actualDistanceSiteToSite: item.actualDistanceSiteToSite || null,
              actualDistanceSiteToClosure: item.actualDistanceSiteToClosure || null,
              initialCore: item.initialCore || null,
              projectStatusExcel: item.projectStatusExcel || null
            };
            
            console.log(`  📦 Segment ${idx + 1}:`, segment);
            return segment;
          });
          
          console.log('✅ newFOSegments built:', spkData.newFOSegments.length, 'segments');
        }
      } else {
        console.error('❌ Unknown project type:', formData.projectType);
      }
      
      console.log('='.repeat(80));
      console.log('📤 FINAL SPK PAYLOAD TO BACKEND:');
      console.log(JSON.stringify(spkData, null, 2));
      console.log('='.repeat(80));
      
      // CREATE SPK
      console.log('📡 Calling createSPK API...');
      const spkResponse = await createSPK(spkData);
      console.log('✅ SPK Created Response:', spkResponse);
      
      // Upload documents
      if (documents.length > 0) {
        console.log('📤 Uploading', documents.length, 'documents...');
        toast.loading('📤 Uploading documents...', { id: 'upload' });
        
        const uploadPromises = documents.map(doc => 
          uploadDocument(spkResponse.id, doc.file, doc.description)
        );
        
        await Promise.all(uploadPromises);
        
        toast.success(`✅ ${documents.length} dokumen berhasil diupload!`, { id: 'upload' });
      }
      
      const totalItems = existingItems.length + newItems.length;
      let summaryMessage = `✅ SPK dibuat dengan ${totalItems} items`;
      
      if (existingItems.length > 0 && newItems.length > 0) {
        summaryMessage = `✅ SPK dibuat dengan ${existingItems.length} existing + ${newItems.length} new items`;
      }
      
      if (documents.length > 0) {
        summaryMessage += ` dan ${documents.length} dokumen`;
      }
      
      toast.success(summaryMessage, { duration: 5000 });
      
    } else if (mode === 'edit') {
  console.log('✏️ UPDATE MODE');
  
  // ===== Build base update payload =====
  const updatePayload = {
    spkNumber: spkData.spkNumber,
    spkDate: spkData.spkDate,
    projectType: spkData.projectType,
    projectName: spkData.projectName,
    deliveryPeriod: spkData.deliveryPeriod,
    notes: spkData.notes,
    status: spkData.status
  };
  
  // ============================================================
  // CHECK FOR NEW SEGMENTS/SITES TO ADD
  // ============================================================
  console.log('🔍 Checking items for EDIT mode:', {
    totalItems: items.length,
    projectType: formData.projectType
  });
  
  // Filter: Existing items (database IDs < 1 trillion)
  const existingItems = items.filter(item => {
    const itemId = item.id;
    const isExisting = typeof itemId === 'number' && itemId < 1000000000000;
    if (isExisting) {
      console.log(`  ✅ EXISTING item (DB ID): ${itemId}`);
    }
    return isExisting;
  });
  
  // Filter: New items (timestamp IDs >= 1 trillion)
  const newItems = items.filter(item => {
    const itemId = item.id;
    const isNew = typeof itemId !== 'number' || itemId >= 1000000000000;
    if (isNew) {
      console.log(`  🆕 NEW item (will be created): ${itemId}`);
    }
    return isNew;
  });
  
  console.log('📊 Items breakdown (EDIT mode):', {
    total: items.length,
    existing: existingItems.length,
    new: newItems.length
  });
  
  // ============================================================
  // ADD NEW SEGMENTS/SITES TO UPDATE PAYLOAD
  // ============================================================
  
  if (formData.projectType === ProjectType.TOWER) {
    console.log('🏗️ TOWER Project - Checking for new sites...');
    
    if (newItems.length > 0) {
      updatePayload.newTowerSites = newItems.map((item, idx) => {
        const site = {
          siteId: item.siteId,
          siteName: item.siteName,
          location: item.location,
          regional: item.regional,
          towerHeight: parseInt(item.towerHeight),
          typeSiteContract: item.typeSiteContract
        };
        console.log(`  🏗️ New Tower Site ${idx + 1}:`, site);
        return site;
      });
      
      console.log(`✅ Will create ${updatePayload.newTowerSites.length} new tower sites`);
    } else {
      console.log('ℹ️ No new tower sites to add');
    }
    
  } else if (formData.projectType === ProjectType.FO) {
    console.log('📡 FO Project - Checking for new segments...');
    
    if (newItems.length > 0) {
      updatePayload.newFOSegments = newItems.map((item, idx) => {
        const segment = {
          // ============= MANDATORY FIELDS (6) =============
          spanId: item.spanId,
          segmentName: item.segmentName,
          area: item.area,
          kota: item.kota,
          length: parseInt(item.length),
          ringType: item.ringType,
          
          // ============= OPTIONAL FIELDS (14) =============
          segmentId: item.segmentId || null,
          ringId: item.ringId || null,
          ringStatus: item.ringStatus || null,
          spkIdReference: item.spkIdReference || null,
          operator: item.operator || null,
          pulau: item.pulau || null,
          contractOperator: item.contractOperator || null,
          spkDate: item.spkDate || null,
          planDistanceSiteToSite: item.planDistanceSiteToSite || null,
          planDistanceSiteToClosure: item.planDistanceSiteToClosure || null,
          actualDistanceSiteToSite: item.actualDistanceSiteToSite || null,
          actualDistanceSiteToClosure: item.actualDistanceSiteToClosure || null,
          initialCore: item.initialCore || null,
          projectStatusExcel: item.projectStatusExcel || null
        };
        
        console.log(`  📡 New FO Segment ${idx + 1}:`, segment);
        return segment;
      });
      
      // Count how many have optional fields
      const withOptionalFields = updatePayload.newFOSegments.filter(seg => 
        seg.segmentId || seg.ringId || seg.operator || seg.initialCore
      ).length;
      
      console.log(`✅ Will create ${updatePayload.newFOSegments.length} new FO segments`);
      console.log(`   → ${withOptionalFields} segments with optional fields`);
      
    } else {
      console.log('ℹ️ No new FO segments to add');
    }
  }
  
  // ============================================================
  // SEND UPDATE REQUEST
  // ============================================================
  console.log('='.repeat(80));
  console.log('📤 FINAL UPDATE PAYLOAD:');
  console.log(JSON.stringify(updatePayload, null, 2));
  console.log('='.repeat(80));
  console.log('📤 SPK ID:', formData.id);
  
  // Update SPK
  console.log('📡 Calling updateSPK API...');
  const spkResponse = await updateSPK(formData.id, updatePayload);
  console.log('✅ SPK Updated Response:', spkResponse);
  
  // Success message
  let successMessage = '✅ SPK berhasil diupdate!';
  
  if (newItems.length > 0) {
    successMessage = `✅ SPK diupdate dengan ${newItems.length} ${
      formData.projectType === ProjectType.TOWER ? 'tower sites' : 'segments'
    } baru!`;
  }
  
  toast.success(successMessage, { duration: 5000 });
}
    
    console.log('🎉 SUBMIT COMPLETED SUCCESSFULLY');
    
    if (onBack) {
      onBack();
    }
    
  } catch (error) {
    console.error('='.repeat(80));
    console.error('❌ ERROR IN SUBMIT:');
    console.error('Error object:', error);
    console.error('Error message:', error.message);
    console.error('Error response:', error.response);
    console.error('='.repeat(80));
    
    let errorMessage = 'Gagal menyimpan SPK. Silakan coba lagi.';
    
    if (error.response?.data?.detail) {
      errorMessage = error.response.data.detail;
    } else if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    toast.error(errorMessage, { duration: 6000 });
  } finally {
    setIsSubmitting(false);
  }
};

  const handleReset = () => {
    setFormData({
      customerId: null,
      spkNumber: '',
      spkDate: '',
      projectType: ProjectType.TOWER,
      projectName: '',
      deliveryPeriod: null,
      notes: '',
      status: SPKStatus.ACCEPTED,
      towerSiteIds: [],
      foSegmentIds: []
    });
    setDocuments([]);
    setItems([]);
    setErrors({});
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={onBack} 
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">
                {mode === 'add' ? 'Add New SPK' : 'Edit SPK'}
              </h1>
              <p className="text-gray-600 mt-1">
                {mode === 'add' ? 'Fill in the form below to create a new SPK' : 'Update SPK information'}
              </p>
            </div>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Panel 1: SPK Basic Information */}
        <div className="bg-white rounded-lg shadow-md border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-red-50">
            <h2 className="text-lg font-bold text-gray-800">SPK Information</h2>
            <p className="text-sm text-gray-600 mt-1">Basic information about the work order</p>
          </div>
          
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Customer Select */}
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Customer <span className="text-red-600">*</span>
                </label>
                <select
                  name="customerId"
                  value={formData.customerId || ''}
                  onChange={handleChange}
                  disabled={mode === 'edit'}
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all ${
                    errors.customerId ? 'border-red-500 bg-red-50' : 'border-gray-300'
                  } ${mode === 'edit' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                >
                  <option value="">-- Select Customer --</option>
                  {customers.map(customer => (
                    <option key={customer.id} value={customer.id}>
                      {customer.customerCode} - {customer.customerName}
                    </option>
                  ))}
                </select>
                {errors.customerId && (
                  <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                    <span>⚠</span> {errors.customerId}
                  </p>
                )}
              </div>

              {/* SPK Number */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  SPK Number <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  name="spkNumber"
                  value={formData.spkNumber}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all ${
                    errors.spkNumber ? 'border-red-500 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="e.g., SPK/2025/001"
                />
                {errors.spkNumber && (
                  <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                    <span>⚠</span> {errors.spkNumber}
                  </p>
                )}
              </div>

              {/* SPK Date */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  SPK Date <span className="text-red-600">*</span>
                </label>
                <input
                  type="date"
                  name="spkDate"
                  value={formData.spkDate}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all ${
                    errors.spkDate ? 'border-red-500 bg-red-50' : 'border-gray-300'
                  }`}
                />
                {errors.spkDate && (
                  <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                    <span>⚠</span> {errors.spkDate}
                  </p>
                )}
              </div>

              {/* Project Type */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Project Type <span className="text-red-600">*</span>
                </label>
                <select
                  name="projectType"
                  value={formData.projectType}
                  onChange={handleChange}
                  disabled={mode === 'edit'}
                  className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all ${
                    mode === 'edit' ? 'bg-gray-100 cursor-not-allowed' : ''
                  }`}
                >
                  <option value={ProjectType.TOWER}>TOWER</option>
                  <option value={ProjectType.FO}>FO (Fiber Optic)</option>
                </select>
              </div>

              {/* Delivery Period */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Delivery Period (Months) <span className="text-red-600">*</span>
                </label>
                <input
                  type="number"
                  name="deliveryPeriod"
                  value={formData.deliveryPeriod || ''}
                  onChange={handleChange}
                  min="1"
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all ${
                    errors.deliveryPeriod ? 'border-red-500 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="e.g., 6"
                />
                {errors.deliveryPeriod && (
                  <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                    <span>⚠</span> {errors.deliveryPeriod}
                  </p>
                )}
              </div>

              {/* Status (Edit mode only) */}
              {mode === 'edit' && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Status <span className="text-red-600">*</span>
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                  >
                    <option value={SPKStatus.ACCEPTED}>Accepted</option>
                    <option value={SPKStatus.CONVERTED}>Converted to Project</option>
                    <option value={SPKStatus.DELIVERED}>Delivered</option>
                  </select>
                </div>
              )}

              {/* Project Name */}
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Project Name <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  name="projectName"
                  value={formData.projectName}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all ${
                    errors.projectName ? 'border-red-500 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="e.g., Telkomsel Jakarta Tower Project"
                />
                {errors.projectName && (
                  <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                    <span>⚠</span> {errors.projectName}
                  </p>
                )}
              </div>

              {/* Notes */}
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Notes
                </label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  rows="3"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all resize-none"
                  placeholder="Additional notes or special instructions..."
                />
              </div>

              {/* Documents Upload Section */}
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  SPK Documents <span className="text-red-600">*</span>
                </label>
                
                {errors.documents && (
                  <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-600 flex items-center gap-1">
                      <span>⚠</span> {errors.documents}
                    </p>
                  </div>
                )}

                {/* Existing Documents (Edit mode) */}
                {mode === 'edit' && existingDocuments.length > 0 && (
                  <div className="mb-4">
                    <p className="text-sm text-gray-600 mb-2">Existing Documents:</p>
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gradient-to-r from-blue-600 to-blue-500">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider w-12">
                              No
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">
                              Document Name
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">
                              Description
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {existingDocuments.map((doc, index) => (
                            <tr key={doc.id} className="hover:bg-blue-50 transition-colors">
                              <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                                {index + 1}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <div className="flex items-center gap-2">
                                  <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                                    <FileText className="w-4 h-4 text-white" />
                                  </div>
                                  <div>
                                    <p className="text-sm font-semibold text-gray-900">{doc.fileName}</p>
                                    <p className="text-xs text-gray-500">{doc.fileType}</p>
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <p className="text-sm text-gray-700">{doc.description || '-'}</p>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* New Documents (to be uploaded) */}
                {documents.length > 0 && (
                  <div className="mb-4 border border-gray-200 rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gradient-to-r from-green-600 to-green-500">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider w-12">
                            No
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">
                            Document Name (New)
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">
                            Description
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider w-24">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {documents.map((doc, index) => (
                          <tr key={doc.id} className="hover:bg-green-50 transition-colors">
                            <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                              {index + 1}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center flex-shrink-0">
                                  <FileText className="w-4 h-4 text-white" />
                                </div>
                                <div>
                                  <p className="text-sm font-semibold text-gray-900">{doc.fileName}</p>
                                  <p className="text-xs text-gray-500">{doc.fileType}</p>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <p className="text-sm text-gray-700">{doc.description || '-'}</p>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <button
                                type="button"
                                onClick={() => handleRemoveDocument(doc.id)}
                                className="p-1.5 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                                title="Remove"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Add Document Button */}
                {mode === 'add' && (
                  <button
                    type="button"
                    onClick={() => setShowDocumentModal(true)}
                    className="w-full border-2 border-dashed border-blue-300 hover:border-blue-500 rounded-lg p-6 transition-all hover:bg-blue-50"
                  >
                    <div className="flex flex-col items-center gap-2">
                      <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                        <Plus className="w-6 h-6 text-blue-600" />
                      </div>
                      <div className="text-sm font-medium text-blue-600">
                        Add Document
                      </div>
                      <p className="text-xs text-gray-500">
                        Upload SPK or supporting documents
                      </p>
                    </div>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Panel 2: Sites/Segments Management - TO BE CONTINUED IN NEXT FILE DUE TO LENGTH */}
{/* Panel 2: Sites/Segments Management */}
        <div className="bg-white rounded-lg shadow-md border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-red-50 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-gray-800">
                {formData.projectType === ProjectType.TOWER ? 'Tower Sites' : 'FO Segments'}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Add {formData.projectType === ProjectType.TOWER ? 'tower sites' : 'fiber optic segments'} for this SPK
              </p>
            </div>
            <div className="flex items-center gap-2">
              {/* Excel Upload Buttons - Only show for FO type */}
              {formData.projectType === ProjectType.FO && (
                <>
                  <button
                    type="button"
                    onClick={handleDownloadTemplate}
                    className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50 font-medium px-4 py-2 rounded-lg flex items-center gap-2 transition-all shadow-sm hover:shadow-md"
                    title="Download Excel Template"
                  >
                    <Download className="w-4 h-4" />
                    Download Template
                  </button>
                  <label className="border-2 border-green-600 text-green-600 hover:bg-green-50 font-medium px-4 py-2 rounded-lg flex items-center gap-2 transition-all shadow-sm hover:shadow-md cursor-pointer">
                    <FileSpreadsheet className="w-4 h-4" />
                    Upload from Excel
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleExcelUpload}
                      className="hidden"
                    />
                  </label>
                </>
              )}
              <button
                type="button"
                onClick={() => setShowAddModal(true)}
                className="bg-red-600 hover:bg-red-700 text-white font-medium px-4 py-2 rounded-lg flex items-center gap-2 transition-all shadow-md hover:shadow-lg"
              >
                <Plus className="w-4 h-4" />
                Add {formData.projectType === ProjectType.TOWER ? 'Site' : 'Segment'}
              </button>
            </div>
          </div>

          <div className="p-6">
            {errors.items && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600 flex items-center gap-1">
                  <span>⚠</span> {errors.items}
                </p>
              </div>
            )}

            {items.length === 0 ? (
              <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
                <div className="w-16 h-16 mx-auto bg-gray-200 rounded-full flex items-center justify-center mb-4">
                  {formData.projectType === ProjectType.TOWER ? (
                    <Building2 className="w-8 h-8 text-gray-400" />
                  ) : (
                    <Navigation className="w-8 h-8 text-gray-400" />
                  )}
                </div>
                <p className="text-gray-600 font-medium">
                  No {formData.projectType === ProjectType.TOWER ? 'tower sites' : 'segments'} added yet
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  Click "Add {formData.projectType === ProjectType.TOWER ? 'Site' : 'Segment'}" button or upload Excel to add items
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {items.map((item, idx) => (
                  <ItemCard
                    key={item.id}
                    item={item}
                    index={idx}
                    projectType={formData.projectType}
                    onRemove={handleRemoveItem}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Form Actions */}
        <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
          <div className="flex flex-wrap items-center justify-end gap-3">
            <button 
              type="button" 
              onClick={handleReset} 
              className="border border-gray-300 text-gray-700 hover:bg-gray-100 font-medium px-6 py-2 rounded-lg transition-colors flex items-center gap-2"
              disabled={isSubmitting}
            >
              <RotateCcw className="w-4 h-4" />
              Reset
            </button>
            <button 
              type="button" 
              onClick={onBack} 
              className="border border-gray-300 text-gray-700 hover:bg-gray-100 font-medium px-6 py-2 rounded-lg transition-colors"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="bg-red-600 hover:bg-red-700 text-white font-medium px-6 py-2 rounded-lg transition-all shadow-md hover:shadow-lg flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isSubmitting}
            >
              <Save className="w-5 h-5" />
              {isSubmitting ? 'Saving...' : (mode === 'add' ? 'Create SPK' : 'Update SPK')}
            </button>
          </div>
        </div>
      </form>

      {/* Add Document Modal */}
      {showDocumentModal && (
        <AddDocumentModal
          onAdd={handleAddDocument}
          onClose={() => setShowDocumentModal(false)}
        />
      )}

      {/* Add Item Modal */}
      {showAddModal && (
        <AddItemModal
          projectType={formData.projectType}
          onAdd={handleAddItem}
          onClose={() => setShowAddModal(false)}
        />
      )}

      {/* Import Summary Modal */}
      {showImportSummary && importResult && (
        <ImportSummaryModal
          result={importResult}
          onClose={() => setShowImportSummary(false)}
        />
      )}
    </div>
  );
};

// Item Card Component
const ItemCard = ({ item, index, projectType, onRemove }) => {
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow group">
      {projectType === ProjectType.TOWER ? (
        // Tower Site Card
        <div className="p-4 hover:bg-gray-50 transition-colors">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4 flex-1">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold shadow-md bg-gradient-to-br from-purple-600 to-purple-700">
                {index + 1}
              </div>
              <div className="flex-1">
                <div className="font-semibold text-gray-900">{item.siteName}</div>
                <div className="text-sm text-gray-600 flex items-center gap-2 mt-1">
                  <span>{item.siteId}</span>
                  <span>•</span>
                  <span>{item.location}</span>
                  <span>•</span>
                  <span>{item.regional}</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <div className="font-bold text-gray-900">{item.towerHeight} m</div>
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-800 mt-1">
                  {item.typeSiteContract}
                </span>
              </div>
              <button
                type="button"
                onClick={() => onRemove(item.id)}
                className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                title="Remove"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      ) : (
        // FO Segment Card
        <>
          {/* Header Section */}
          <div className="bg-gradient-to-r from-green-50 to-blue-50 px-4 py-3 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-green-600 to-green-700 rounded-lg flex items-center justify-center text-white font-bold shadow-md">
                {index + 1}
              </div>
              <div className="flex-1">
                <div className="font-bold text-gray-900 text-base">{item.segmentName}</div>
                <div className="flex items-center gap-2 mt-1">
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-green-600 text-white">
                    {item.ringType}
                  </span>
                  <span className="text-xs text-gray-600">Span ID: {item.spanId}</span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <div className="text-xl font-bold text-green-600">
                    {(item.length / 1000).toFixed(2)} km
                  </div>
                  <div className="text-xs text-gray-500">
                    {item.length.toLocaleString()} m
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => onRemove(item.id)}
                  className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                  title="Remove"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
          
          {/* Details Section */}
          <div className="p-4 bg-white">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Area / Province</label>
                  <p className="text-sm font-semibold text-gray-900">{item.area}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Kota / Kabupaten</label>
                  <p className="text-sm font-semibold text-gray-900">{item.kota}</p>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// Add Item Modal Component
const AddItemModal = ({ projectType, onAdd, onClose }) => {
  const [formData, setFormData] = useState(
    projectType === ProjectType.TOWER
      ? {
          siteId: '',
          siteName: '',
          location: '',
          regional: '',
          towerHeight: '',
          typeSiteContract: 'MACRO'
        }
      : {
          spanId: '',
          segmentName: '',
          area: '',
          kota: '',
          length: '',
          ringType: 'Insert'
        }
  );

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (projectType === ProjectType.TOWER) {
      if (!formData.siteId.trim()) newErrors.siteId = 'Site ID is required';
      if (!formData.siteName.trim()) newErrors.siteName = 'Site name is required';
      if (!formData.location.trim()) newErrors.location = 'Location is required';
      if (!formData.regional.trim()) newErrors.regional = 'Regional is required';
      if (!formData.towerHeight || formData.towerHeight <= 0) {
        newErrors.towerHeight = 'Tower height must be greater than 0';
      }
    } else {
      if (!formData.spanId.trim()) newErrors.spanId = 'Span ID is required';
      if (!formData.segmentName.trim()) newErrors.segmentName = 'Segment name is required';
      if (!formData.area) newErrors.area = 'Area/Province is required';
      if (!formData.kota.trim()) newErrors.kota = 'Kota/Kabupaten is required';
      if (!formData.length || formData.length <= 0) {
        newErrors.length = 'Length must be greater than 0';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onAdd(formData);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-red-50">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-xl font-bold text-gray-800">
                Add {projectType === ProjectType.TOWER ? 'Tower Site' : 'FO Segment'}
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Fill in the details below
              </p>
            </div>
            <button 
              onClick={onClose} 
              className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {projectType === ProjectType.TOWER ? (
              <>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Site ID <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    name="siteId"
                    value={formData.siteId}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 ${
                      errors.siteId ? 'border-red-500 bg-red-50' : 'border-gray-300'
                    }`}
                    placeholder="e.g., ETJI0219"
                  />
                  {errors.siteId && <p className="mt-1 text-sm text-red-600">{errors.siteId}</p>}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Site Name <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    name="siteName"
                    value={formData.siteName}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 ${
                      errors.siteName ? 'border-red-500 bg-red-50' : 'border-gray-300'
                    }`}
                    placeholder="e.g., JKT-North-001"
                  />
                  {errors.siteName && <p className="mt-1 text-sm text-red-600">{errors.siteName}</p>}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Location <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 ${
                      errors.location ? 'border-red-500 bg-red-50' : 'border-gray-300'
                    }`}
                    placeholder="e.g., Kelapa Gading"
                  />
                  {errors.location && <p className="mt-1 text-sm text-red-600">{errors.location}</p>}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Regional <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    name="regional"
                    value={formData.regional}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 ${
                      errors.regional ? 'border-red-500 bg-red-50' : 'border-gray-300'
                    }`}
                    placeholder="e.g., JAKARTA"
                  />
                  {errors.regional && <p className="mt-1 text-sm text-red-600">{errors.regional}</p>}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Tower Height (m) <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="number"
                    name="towerHeight"
                    value={formData.towerHeight}
                    onChange={handleChange}
                    min="1"
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 ${
                      errors.towerHeight ? 'border-red-500 bg-red-50' : 'border-gray-300'
                    }`}
                    placeholder="e.g., 52"
                  />
                  {errors.towerHeight && <p className="mt-1 text-sm text-red-600">{errors.towerHeight}</p>}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Type Site Contract <span className="text-red-600">*</span>
                  </label>
                  <select
                    name="typeSiteContract"
                    value={formData.typeSiteContract}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    <option value="MACRO">MACRO</option>
                    <option value="MICRO">MICRO</option>
                    <option value="INDOOR">INDOOR</option>
                  </select>
                </div>
              </>
            ) : (
              <>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Span ID <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    name="spanId"
                    value={formData.spanId}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 ${
                      errors.spanId ? 'border-red-500 bg-red-50' : 'border-gray-300'
                    }`}
                    placeholder="e.g., S000051315"
                  />
                  {errors.spanId && <p className="mt-1 text-sm text-red-600">{errors.spanId}</p>}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Segment Name <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    name="segmentName"
                    value={formData.segmentName}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 ${
                      errors.segmentName ? 'border-red-500 bg-red-50' : 'border-gray-300'
                    }`}
                    placeholder="e.g., Site A <> Site B"
                  />
                  {errors.segmentName && <p className="mt-1 text-sm text-red-600">{errors.segmentName}</p>}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Area / Province <span className="text-red-600">*</span>
                  </label>
                  <select
                    name="area"
                    value={formData.area}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 ${
                      errors.area ? 'border-red-500 bg-red-50' : 'border-gray-300'
                    }`}
                  >
                    <option value="">-- Select Province --</option>
                    <option value="DKI Jakarta">DKI Jakarta</option>
                    <option value="Jawa Barat">Jawa Barat</option>
                    <option value="Jawa Tengah">Jawa Tengah</option>
                    <option value="Jawa Timur">Jawa Timur</option>
                    <option value="Banten">Banten</option>
                    <option value="Sumatera Utara">Sumatera Utara</option>
                    <option value="Sumatera Barat">Sumatera Barat</option>
                    <option value="Sumatera Selatan">Sumatera Selatan</option>
                    <option value="Lampung">Lampung</option>
                    <option value="Bali">Bali</option>
                    <option value="Kalimantan Timur">Kalimantan Timur</option>
                    <option value="Kalimantan Selatan">Kalimantan Selatan</option>
                    <option value="Sulawesi Selatan">Sulawesi Selatan</option>
                    <option value="Sulawesi Utara">Sulawesi Utara</option>
                    <option value="Papua">Papua</option>
                  </select>
                  {errors.area && <p className="mt-1 text-sm text-red-600">{errors.area}</p>}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Kota / Kabupaten <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    name="kota"
                    value={formData.kota}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 ${
                      errors.kota ? 'border-red-500 bg-red-50' : 'border-gray-300'
                    }`}
                    placeholder="e.g., Kota Bandung"
                  />
                  {errors.kota && <p className="mt-1 text-sm text-red-600">{errors.kota}</p>}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Length (meters) <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="number"
                    name="length"
                    value={formData.length}
                    onChange={handleChange}
                    min="1"
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 ${
                      errors.length ? 'border-red-500 bg-red-50' : 'border-gray-300'
                    }`}
                    placeholder="e.g., 45000"
                  />
                  {errors.length && <p className="mt-1 text-sm text-red-600">{errors.length}</p>}
                  <p className="text-xs text-gray-500 mt-1">Enter length in meters</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Ring Type <span className="text-red-600">*</span>
                  </label>
                  <select
                    name="ringType"
                    value={formData.ringType}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    <option value="Insert">Insert</option>
                    <option value="Add Span">Add Span</option>
                    <option value="Star">Star</option>
                  </select>
                </div>
              </>
            )}
          </div>

          <div className="mt-6 flex justify-end gap-3 pt-6 border-t border-gray-200">
            <button 
              type="button" 
              onClick={onClose} 
              className="border border-gray-300 text-gray-700 hover:bg-gray-100 font-medium px-6 py-2 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="bg-red-600 hover:bg-red-700 text-white font-medium px-6 py-2 rounded-lg transition-all shadow-md hover:shadow-lg flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Add {projectType === ProjectType.TOWER ? 'Site' : 'Segment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Add Document Modal Component
const AddDocumentModal = ({ onAdd, onClose }) => {
  const [formData, setFormData] = useState({
    file: null,
    fileName: '',
    fileType: '',
    fileUrl: null,
    description: ''
  });
  const [fileError, setFileError] = useState('');
  const [errors, setErrors] = useState({});

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      setFileError('Only PDF, JPG, and PNG files are allowed');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setFileError('File size must be less than 5MB');
      return;
    }

    // Get file type label
    let fileTypeLabel = 'PDF';
    if (file.type.includes('image')) {
      fileTypeLabel = file.type.split('/')[1].toUpperCase();
    }

    const fileUrl = URL.createObjectURL(file);
    setFormData(prev => ({
      ...prev,
      file: file,  // Store actual File object
      fileName: file.name,
      fileType: fileTypeLabel,
      fileUrl: fileUrl
    }));
    setFileError('');
    if (errors.file) {
      setErrors(prev => ({ ...prev, file: '' }));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleRemoveFile = () => {
    setFormData(prev => ({
      ...prev,
      file: null,
      fileName: '',
      fileType: '',
      fileUrl: null
    }));
    document.getElementById('docFileInput').value = '';
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.file) newErrors.file = 'Document file is required';
    if (!formData.description.trim()) newErrors.description = 'Description is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onAdd(formData);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-red-50">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-xl font-bold text-gray-800">Add Document</h3>
              <p className="text-sm text-gray-600 mt-1">Upload SPK or supporting document</p>
            </div>
            <button 
              onClick={onClose} 
              className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {/* File Upload */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Document File <span className="text-red-600">*</span>
            </label>
            
            {!formData.fileUrl ? (
              <div className={`border-2 border-dashed rounded-lg p-6 transition-all ${
                errors.file || fileError ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-blue-400'
              }`}>
                <div className="text-center">
                  <div className="w-16 h-16 mx-auto bg-blue-100 rounded-full flex items-center justify-center mb-4">
                    <Upload className="w-8 h-8 text-blue-600" />
                  </div>
                  <div className="mb-4">
                    <label 
                      htmlFor="docFileInput" 
                      className="cursor-pointer bg-blue-600 hover:bg-blue-700 text-white font-medium px-6 py-3 rounded-lg inline-flex items-center gap-2 transition-all shadow-md hover:shadow-lg"
                    >
                      <Upload className="w-4 h-4" />
                      Select File
                    </label>
                    <input
                      id="docFileInput"
                      type="file"
                      onChange={handleFileSelect}
                      accept=".pdf,.jpg,.jpeg,.png"
                      className="hidden"
                    />
                  </div>
                  <p className="text-sm text-gray-600">
                    Click to upload document
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    PDF, JPG, or PNG (Max 5MB)
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between bg-gradient-to-r from-blue-50 to-green-50 p-4 rounded-lg border border-blue-200">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
                    <FileText className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{formData.fileName}</p>
                    <p className="text-xs text-green-600 flex items-center gap-1 mt-1">
                      <span>✓</span> File selected ({formData.fileType})
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <a 
                    href={formData.fileUrl} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium hover:underline transition-colors"
                  >
                    Preview
                  </a>
                  <button 
                    type="button" 
                    onClick={handleRemoveFile} 
                    className="p-2 hover:bg-red-100 rounded-lg transition-colors group"
                  >
                    <X className="w-5 h-5 text-red-500 group-hover:text-red-700" />
                  </button>
                </div>
              </div>
            )}
            
            {(errors.file || fileError) && (
              <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                <span>⚠</span> {errors.file || fileError}
              </p>
            )}
          </div>

          {/* Description */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Document Description <span className="text-red-600">*</span>
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="3"
              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none ${
                errors.description ? 'border-red-500 bg-red-50' : 'border-gray-300'
              }`}
              placeholder="e.g., SPK Document - Original, Addendum 1, Technical Drawing, etc."
            />
            {errors.description && (
              <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                <span>⚠</span> {errors.description}
              </p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              Provide a brief description of this document
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button 
              type="button" 
              onClick={onClose} 
              className="border border-gray-300 text-gray-700 hover:bg-gray-100 font-medium px-6 py-2 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="bg-red-600 hover:bg-red-700 text-white font-medium px-6 py-2 rounded-lg transition-all shadow-md hover:shadow-lg flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Add Document
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Import Summary Modal Component
const ImportSummaryModal = ({ result, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-red-50">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-xl font-bold text-gray-800">Import Summary</h3>
              <p className="text-sm text-gray-600 mt-1">Excel import results</p>
            </div>
            <button 
              onClick={onClose} 
              className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          {/* Summary Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-900">{result.success.length}</p>
                  <p className="text-sm text-green-700 font-medium">Success</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-yellow-600 rounded-full flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-yellow-900">{result.skipped.length}</p>
                  <p className="text-sm text-yellow-700 font-medium">Skipped</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-red-50 to-red-100 border border-red-200 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center">
                  <XCircle className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-red-900">{result.errors.length}</p>
                  <p className="text-sm text-red-700 font-medium">Errors</p>
                </div>
              </div>
            </div>
          </div>

          {/* Success Details */}
          {result.success.length > 0 && (
            <div className="mb-6">
              <h4 className="text-lg font-bold text-green-900 mb-3 flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                Successfully Imported ({result.success.length})
              </h4>
              <div className="bg-green-50 border border-green-200 rounded-lg overflow-hidden">
                <div className="max-h-48 overflow-y-auto">
                  <table className="min-w-full divide-y divide-green-200">
                    <thead className="bg-green-100 sticky top-0">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-green-900">Row</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-green-900">Span ID</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-green-900">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-green-200 bg-white">
                      {result.success.map((item, idx) => (
                        <tr key={idx} className="hover:bg-green-50">
                          <td className="px-4 py-2 text-sm text-gray-900">{item.row}</td>
                          <td className="px-4 py-2 text-sm font-medium text-gray-900">{item.identifier}</td>
                          <td className="px-4 py-2 text-sm">
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                              item.action === 'Updated' 
                                ? 'bg-blue-100 text-blue-800' 
                                : 'bg-green-100 text-green-800'
                            }`}>
                              {item.action}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Skipped Details */}
          {result.skipped.length > 0 && (
            <div className="mb-6">
              <h4 className="text-lg font-bold text-yellow-900 mb-3 flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                Skipped Rows ({result.skipped.length})
              </h4>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg overflow-hidden">
                <div className="max-h-48 overflow-y-auto">
                  <table className="min-w-full divide-y divide-yellow-200">
                    <thead className="bg-yellow-100 sticky top-0">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-yellow-900">Row</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-yellow-900">Identifier</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-yellow-900">Reason</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-yellow-200 bg-white">
                      {result.skipped.map((item, idx) => (
                        <tr key={idx} className="hover:bg-yellow-50">
                          <td className="px-4 py-2 text-sm text-gray-900">{item.row}</td>
                          <td className="px-4 py-2 text-sm font-medium text-gray-900">{item.identifier}</td>
                          <td className="px-4 py-2 text-sm text-gray-700">{item.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Error Details */}
          {result.errors.length > 0 && (
            <div className="mb-6">
              <h4 className="text-lg font-bold text-red-900 mb-3 flex items-center gap-2">
                <XCircle className="w-5 h-5" />
                Errors ({result.errors.length})
              </h4>
              <div className="bg-red-50 border border-red-200 rounded-lg overflow-hidden">
                <div className="max-h-48 overflow-y-auto">
                  <table className="min-w-full divide-y divide-red-200">
                    <thead className="bg-red-100 sticky top-0">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-red-900">Row</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-red-900">Identifier</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-red-900">Reason</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-red-200 bg-white">
                      {result.errors.map((item, idx) => (
                        <tr key={idx} className="hover:bg-red-50">
                          <td className="px-4 py-2 text-sm text-gray-900">{item.row}</td>
                          <td className="px-4 py-2 text-sm font-medium text-gray-900">{item.identifier}</td>
                          <td className="px-4 py-2 text-sm text-gray-700">{item.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex justify-end">
            <button 
              onClick={onClose} 
              className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-6 py-2 rounded-lg transition-all shadow-md hover:shadow-lg"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SPKForm;        